import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface LeagueRankingData {
  position: number;
  registrationId: string;
  player1Name: string;
  player2Name?: string;
  matchesWon: number;
  matchesLost: number;
  gamesWon: number;
  gamesLost: number;
  difference: number;
  isCurrentUser: boolean;
  // Fields for ladder leagues
  movementLabel?: string;
  movementType?: string;
  challengeStatus?: string;
  challengeTarget?: number;
}

// Calculate movement based on position comparison between encounters
const calculateMovement = (
  registrationId: string,
  currentPosition: number,
  encounterNumber: number,
  snapshots: Record<string, Record<string, number>> | null,
  registrations?: Array<{ id: string; team_order: number | null }>
): { movementLabel: string; movementType: string } => {
  // Encuentro 0 = Ranking Inicial, no hay movimiento
  if (encounterNumber === 0 || !snapshots) {
    return { movementLabel: 'Mantiene', movementType: 'neutral' };
  }

  // Para Encuentro 1, comparar con team_order (ranking inicial)
  if (encounterNumber === 1) {
    if (!registrations) {
      return { movementLabel: 'Mantiene', movementType: 'neutral' };
    }
    const registration = registrations.find(r => r.id === registrationId);
    const initialPosition = registration?.team_order || currentPosition;
    const diff = initialPosition - currentPosition;
    
    if (diff > 0) {
      return { movementLabel: `+${diff}`, movementType: 'up' };
    } else if (diff < 0) {
      return { movementLabel: `${diff}`, movementType: 'down' };
    }
    return { movementLabel: 'Mantiene', movementType: 'neutral' };
  }

  // Para Encuentro 2+, comparar con snapshot anterior
  const previousSnapshot = snapshots[(encounterNumber - 1).toString()];
  if (!previousSnapshot) {
    return { movementLabel: 'Mantiene', movementType: 'neutral' };
  }

  const previousPosition = previousSnapshot[registrationId];
  if (!previousPosition) {
    return { movementLabel: 'Mantiene', movementType: 'neutral' };
  }

  const diff = previousPosition - currentPosition;
  if (diff > 0) {
    return { movementLabel: `+${diff}`, movementType: 'up' };
  } else if (diff < 0) {
    return { movementLabel: `${diff}`, movementType: 'down' };
  }
  return { movementLabel: 'Mantiene', movementType: 'neutral' };
};

// Generate challenges based on challenge type (simple: even challenges odd)
const generateChallenges = (
  position: number,
  challengeType: string
): { challengeStatus: string; challengeTarget?: number } => {
  if (challengeType === 'simple') {
    // Even challenges Odd: 2->1, 4->3, 6->5
    if (position % 2 === 0) {
      return {
        challengeStatus: 'challenger',
        challengeTarget: position - 1
      };
    } else {
      return { challengeStatus: 'challenged' };
    }
  }
  return { challengeStatus: 'none' };
};

export function useLeagueRanking(eventId: string, encounterNumber: number, totalEncountersOverride?: number, subcategory?: string) {
  const { user } = useAuth();
  
  // Fetch POSITION data (where player was BEFORE the encounter)
  const { data: positionData, isLoading: isLoadingPositions } = useQuery({
    queryKey: ['league-ladder-positions', eventId, encounterNumber, subcategory],
    queryFn: async () => {
      // Encuentro 0 (Ranking Inicial): buscar 'before' del encuentro 1
      if (encounterNumber === 0) {
        const { data, error } = await supabase
          .from('league_ladder_rankings')
          .select('*')
          .eq('event_id', eventId)
          .eq('encounter_number', 1)
          .eq('snapshot_type', 'before')
          .order('position', { ascending: true });
        if (error) throw error;
        return data || [];
      }
      
      // Encuentro N: buscar 'before' de N primero
      const { data: beforeData, error: beforeError } = await supabase
        .from('league_ladder_rankings')
        .select('*')
        .eq('event_id', eventId)
        .eq('encounter_number', encounterNumber)
        .eq('snapshot_type', 'before')
        .order('position', { ascending: true });
      if (beforeError) throw beforeError;
      if (beforeData && beforeData.length > 0) return beforeData;
      
      // Fallback: buscar 'after' del encuentro N-1 (posiciones resultantes del encuentro anterior)
      if (encounterNumber > 1) {
        const { data: prevAfterData, error: prevAfterError } = await supabase
          .from('league_ladder_rankings')
          .select('*')
          .eq('event_id', eventId)
          .eq('encounter_number', encounterNumber - 1)
          .eq('snapshot_type', 'after')
          .order('position', { ascending: true });
        if (prevAfterError) throw prevAfterError;
        if (prevAfterData && prevAfterData.length > 0) return prevAfterData;
      }
      
      // Fallback final para encuentro 1: buscar 'before' del encuentro 1
      if (encounterNumber === 1) {
        const { data: enc1Before, error: enc1Error } = await supabase
          .from('league_ladder_rankings')
          .select('*')
          .eq('event_id', eventId)
          .eq('encounter_number', 1)
          .eq('snapshot_type', 'before')
          .order('position', { ascending: true });
        if (enc1Error) throw enc1Error;
        return enc1Before || [];
      }
      
      return [];
    },
    enabled: !!eventId
  });

  // Fetch MOVEMENT data (badges from 'after' snapshot of current encounter)
  const { data: movementData, isLoading: isLoadingMovement } = useQuery({
    queryKey: ['league-ladder-movement', eventId, encounterNumber, subcategory],
    queryFn: async () => {
      // Ranking Inicial: no movement
      if (encounterNumber === 0) return null;
      
      const { data, error } = await supabase
        .from('league_ladder_rankings')
        .select('registration_id, movement_label, movement_type, challenge_status')
        .eq('event_id', eventId)
        .eq('encounter_number', encounterNumber)
        .eq('snapshot_type', 'after');
      if (error) throw error;
      return data && data.length > 0 ? data : null;
    },
    enabled: !!eventId
  });


  // Build a lookup map for movement badges by registration_id
  const movementMap = useMemo(() => {
    if (!movementData) return null;
    const map: Record<string, { movementLabel: string; movementType: string; challengeStatus?: string }> = {};
    for (const m of movementData) {
      map[m.registration_id] = {
        movementLabel: m.movement_label,
        movementType: m.movement_type,
        challengeStatus: m.challenge_status
      };
    }
    return map;
  }, [movementData]);

  // Fetch event rules for encounterSnapshots fallback and challengeType
  const { data: ladderConfig, isLoading: isLoadingSnapshots } = useQuery({
    queryKey: ['event-ladder-config', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('rules')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      
      // Parse ladderConfig from rules JSON
      const rules = data?.rules as any;
      return {
        encounterSnapshots: rules?.ladderConfig?.encounterSnapshots || null,
        challengeType: rules?.ladderConfig?.challengeType || 'simple'
      };
    },
    enabled: !!eventId
  });

  const encounterSnapshots = ladderConfig?.encounterSnapshots || null;
  const challengeType = ladderConfig?.challengeType || 'simple';

  // Fetch registrations as fallback and for user matching, resolving missing names from profiles
  const { data: registrations, isLoading: isLoadingRegistrations } = useQuery({
    queryKey: ['league-registrations', eventId, subcategory],
    queryFn: async () => {
      let query = supabase
        .from('registrations')
        .select('id, user_id, partner_user_id, player1_name, player2_name, team_order')
        .eq('event_id', eventId)
        .eq('status', 'confirmed');
      
      if (subcategory) {
        query = query.eq('subcategory', subcategory);
      }
      
      const { data, error } = await query.order('team_order', { ascending: true });
      
      if (error) throw error;

      // Resolve missing names from profiles
      const enriched = await Promise.all((data || []).map(async (reg) => {
        let player1_name = reg.player1_name;
        let player2_name = reg.player2_name;

        if (!player1_name && reg.user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('user_id', reg.user_id)
            .single();
          player1_name = profile?.full_name || null;
        }
        if (!player2_name && reg.partner_user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('user_id', reg.partner_user_id)
            .single();
          player2_name = profile?.full_name || null;
        }
        return { ...reg, player1_name, player2_name };
      }));

      return enriched;
    },
    enabled: !!eventId
  });

  // Build a set of valid registration IDs for subcategory filtering
  const validRegistrationIds = useMemo(() => {
    if (!subcategory || !registrations?.length) return null;
    return new Set(registrations.map(r => r.id));
  }, [subcategory, registrations]);

  // Fetch event data for total_weeks fallback
  const { data: eventData, isLoading: isLoadingEvent } = useQuery({
    queryKey: ['event-total-weeks', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('total_weeks')
        .eq('id', eventId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!eventId
  });

  // Calculate available encounters - prioritize calendar override for sync
  const availableEncounters = useMemo(() => {
    // Priority 1: Override from calendar for perfect sync
    if (totalEncountersOverride && totalEncountersOverride > 0) {
      return Array.from({ length: totalEncountersOverride }, (_, i) => i + 1);
    }
    
    // Priority 2: Keys from encounterSnapshots
    if (encounterSnapshots) {
      const snapshotKeys = Object.keys(encounterSnapshots).map(k => parseInt(k)).filter(n => !isNaN(n));
      if (snapshotKeys.length > 0) {
        return snapshotKeys.sort((a, b) => a - b);
      }
    }
    
    // Priority 3: total_weeks from event
    const totalWeeks = eventData?.total_weeks || 8;
    return Array.from({ length: totalWeeks }, (_, i) => i + 1);
  }, [eventData?.total_weeks, totalEncountersOverride, encounterSnapshots]);

  // Helper to check if a registration belongs to current user
  const isCurrentUserRegistration = (registrationId: string): boolean => {
    if (!user?.id || !registrations) return false;
    const reg = registrations.find(r => r.id === registrationId);
    return reg ? (reg.user_id === user.id || reg.partner_user_id === user.id) : false;
  };

  // Calculate rankings - prefer ladder_rankings table, then encounterSnapshots, fallback to registrations
  const rankings: LeagueRankingData[] = useMemo(() => {
    // Priority 1: Use league_ladder_rankings table if position data exists
    if (positionData && positionData.length > 0) {
      // Filter by subcategory if needed
      const filteredPositionData = validRegistrationIds
        ? positionData.filter(r => validRegistrationIds.has(r.registration_id))
        : positionData;

      return filteredPositionData.map((ranking, index) => {
        // Get movement from the 'after' snapshot (movementMap)
        const movement = movementMap?.[ranking.registration_id];
        const hasResults = !!movement;
        
        // Resolve missing names from enriched registrations
        let player1Name = ranking.player1_name || ranking.team_name;
        let player2Name = ranking.player2_name || undefined;
        if (registrations?.length) {
          const reg = registrations.find(r => r.id === ranking.registration_id);
          if (reg) {
            if (!player1Name || player1Name === ranking.team_name) {
              player1Name = reg.player1_name || player1Name;
            }
            if (!player2Name && reg.player2_name) {
              player2Name = reg.player2_name;
            }
          }
        }
        
        return {
          position: validRegistrationIds ? index + 1 : ranking.position,
          registrationId: ranking.registration_id,
          player1Name,
          player2Name,
          matchesWon: 0,
          matchesLost: 0,
          gamesWon: 0,
          gamesLost: 0,
          difference: 0,
          isCurrentUser: isCurrentUserRegistration(ranking.registration_id),
          movementLabel: encounterNumber === 0 ? 'Mantiene' : (hasResults ? movement.movementLabel : 'Pendiente'),
          movementType: encounterNumber === 0 ? 'neutral' : (hasResults ? movement.movementType : 'neutral'),
          challengeStatus: hasResults ? movement.challengeStatus : ranking.challenge_status
        };
      });
    }

    // Priority 2: Use encounterSnapshots from event rules JSON
    if (encounterSnapshots && registrations?.length) {
      const targetEncounter = encounterNumber === 0 ? '1' : encounterNumber.toString();
      const snapshot = encounterSnapshots[targetEncounter] as Record<string, number> | undefined;
      
      if (snapshot) {
        return registrations
          .map(reg => {
            const position = snapshot[reg.id] || 999;
            const movement = calculateMovement(reg.id, position, encounterNumber, encounterSnapshots, registrations);
            const challenge = generateChallenges(position, challengeType);
            
            return {
              position,
              registrationId: reg.id,
              player1Name: reg.player1_name || 'Jugador 1',
              player2Name: reg.player2_name || undefined,
              matchesWon: 0,
              matchesLost: 0,
              gamesWon: 0,
              gamesLost: 0,
              difference: 0,
              isCurrentUser: user?.id === reg.user_id || user?.id === reg.partner_user_id,
              movementLabel: movement.movementLabel,
              movementType: movement.movementType,
              challengeStatus: challenge.challengeStatus,
              challengeTarget: challenge.challengeTarget
            };
          })
          .filter(r => r.position !== 999)
          .sort((a, b) => a.position - b.position);
      }
    }

    // Priority 3: Fallback to registrations ordered by team_order
    if (!registrations?.length) return [];

    return registrations.map((reg, index) => {
      const position = validRegistrationIds ? (index + 1) : (reg.team_order || (index + 1));
      const challenge = generateChallenges(position, challengeType);
      
      // Check movementMap for actual movement data from 'after' snapshot
      const movement = movementMap?.[reg.id];
      const hasMovement = !!movement;
      
      let movementLabel = 'Mantiene';
      let movementType = 'neutral';
      
      if (encounterNumber === 0) {
        // Ranking Inicial: always "Mantiene"
        movementLabel = 'Mantiene';
        movementType = 'neutral';
      } else if (hasMovement) {
        // Encounter has results: use actual movement from after snapshot
        movementLabel = movement.movementLabel;
        movementType = movement.movementType;
      } else {
        // Encounter without results yet
        movementLabel = 'Pendiente';
        movementType = 'neutral';
      }
      
      return {
        position,
        registrationId: reg.id,
        player1Name: reg.player1_name || 'Jugador 1',
        player2Name: reg.player2_name || undefined,
        matchesWon: 0,
        matchesLost: 0,
        gamesWon: 0,
        gamesLost: 0,
        difference: 0,
        isCurrentUser: user?.id === reg.user_id || user?.id === reg.partner_user_id,
        movementLabel,
        movementType,
        challengeStatus: hasMovement ? movement.challengeStatus : challenge.challengeStatus,
        challengeTarget: challenge.challengeTarget
      };
    }).sort((a, b) => a.position - b.position);
  }, [positionData, movementMap, encounterSnapshots, registrations, encounterNumber, user?.id, challengeType, validRegistrationIds]);

  // Get encounter status (simplified)
  const getEncounterStatus = (num: number): 'waiting_previous' | 'not_scheduled' | 'completed' | 'in_progress' | 'scheduled' => {
    return 'scheduled';
  };

  return {
    rankings,
    availableEncounters,
    getEncounterStatus,
    isLoading: isLoadingPositions || isLoadingMovement || isLoadingRegistrations || isLoadingEvent || isLoadingSnapshots,
    error: null
  };
}
